<template>
    <Card overlay :loading="loading">
        <template #nav>
            <div :key="spaceId" class="nav-wrap gap-3">
                <DropdownFilter v-model="conversion" :items="conversionItems" :prefix="`${$t('salesSummary.conversion')}: `" :disabled="loading" />

                <Multiselect
                    v-if="showProductGroupsResults"
                    :fetch-options-function="fetchGroups"
                    :placeholder="$t('productGroups')"
                    :selected-options-label="$t('selectedProductGroups')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="groups = $event"
                    @loading="groupsLoading = $event"
                />

                <div class="w-100"></div>

                <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">
                    {{ $t('showProductGroupsResults') }}
                </b-form-checkbox>
            </div>
        </template>

        <DataTable
            ref="dt"
            :items="items"
            :fields="fields"
            :loading="loading"
            :total-rows="total"
            :per-page-values="perPageValues"
            :per-page.sync="perPage"
            :current-page.sync="page"
            :sort-by.sync="sortBy"
            :sort-direction.sync="sortDirection"
            :pagination="true"
            :overflow="true"
            :error="error"
            :header="$t('salesTransactions.title')"
            show-export-button
            header-id="sales-transactions-table"
            @update:error="error = null"
        >
            <template #cell(createdAt)="{ item }">
                {{ $format.date(item.createdAt) }}
            </template>

            <template #cell(conversionTime)="{ item }">
                {{ formatDuration(item.conversionTime) }}
            </template>

            <template #cell(offerPosition)="{ item }">
                {{ item.offerPosition || item.offerPosition === 0 ? item.offerPosition : '—' }}
            </template>

            <template #cell(source)="{ item }">
                {{ item.source || '—' }}
            </template>

            <template #cell(amount)="{ item }">
                {{ $format.currency(item.value) }}
            </template>

            <template #cell(quantity)="{ item }">
                {{ $format.number(item.quantity, 0) }}
            </template>

            <template #cell(share)="{ item }">
                <ShareMultiple
                    :values="[item.ownQuantity, item.otherQuantity, item.unknownQuantityComputed]"
                    :labels="quantityShareLabels"
                    :colors="colors"
                    :value-formatter="$format.number"
                />
            </template>

            <template #cell(details)="{ item }">
                <button type="button" class="btn btn-link btn-sm" :title="$t('salesTransactions.details3')" @click="openConversionPath(item)">
                    <img src="@/assets/icon-transactions.svg" alt="" style="width: 16px; height: 16px">
                </button>
            </template>
        </DataTable>

        <!-- Conversion Path Modal -->
        <b-modal
            v-model="showConversionPathModal"
            :title="$t('conversionPath.title', { id: selectedTransaction?.id || '' })"
            size="xl"
            hide-footer
            dialog-class="wide"
        >
            <ConversionPath v-if="selectedTransaction" :transaction="selectedTransaction" />
        </b-modal>
    </Card>
</template>

<script>
    import Card from '@/components/common/Card.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import ShareMultiple from './ShareMultiple.vue';
    import ConversionPath from './ConversionPath.vue';
    import salesReportMixin from '../salesReportMixin';
    import { SALES_SHARE_COLORS } from '../constants';
    import { formatDuration, fetchGroups } from '../utils';

    export default {
        name: 'SalesTransactions',
        mixins: [salesReportMixin],
        components: { Card, DropdownFilter, Multiselect, DataTable, ShareMultiple, ConversionPath },
        data() {
            return {
                requestTransactions: this.$req('reportsSales/Transactions'),
                requestGroups: this.$req('producerProductGroups/SpaceGroups'),

                groups: [],
                groupsLoading: false,

                rows: [],
                conversion: 24,
                conversionItems: ['all', 24],
                showProductGroupsResults: false,

                page: 1,
                perPage: 10,
                perPageValues: [10, 20, 50, 100],
                sortBy: 'createdAt',
                sortDirection: 'DESC',
                total: 0,
                error: null,
                colors: SALES_SHARE_COLORS,

                showConversionPathModal: false,
                selectedTransaction: null,
            };
        },
        computed: {
            loading() {
                return this.requestTransactions.getLoading() || this.groupsLoading;
            },
            apiParams() {
                const { startDate, endDate } = this.timeRange || {};
                const urlParams = { spaceId: this.spaceId };
                const requestParams = { startDate, endDate };
                if (this.showProductGroupsResults && this.groups?.length) {
                    requestParams.groups = this.groups.join(',');
                }
                if (this.conversion !== 'all') requestParams.conversionTime = Number(this.conversion);
                return { urlParams, requestParams };
            },
            fields() {
                return [
                    {
                        key: 'createdAt',
                        label: this.$t('salesTransactions.date'),
                        sortable: true,
                        class: 'text-left',
                        exportable: true,
                        exportFormatter: value => this.$format.date(value),
                    },
                    { key: 'id', label: this.$t('salesTransactions.transactionId'), sortable: true, class: 'text-left', exportable: true },
                    { key: 'details', label: this.$t('salesTransactions.details3'), sortable: false, class: 'text-center', exportable: false },
                    {
                        key: 'conversionTime',
                        label: this.$t('salesTransactions.conversionTime'),
                        sortable: true,
                        class: 'text-left',
                        exportable: true,
                        exportFormatter: value => this.formatDuration(value),
                    },
                    { key: 'offerPosition', label: this.$t('salesTransactions.offerPosition'), sortable: false, class: 'text-left', exportable: true },
                    { key: 'source', label: this.$t('salesTransactions.source'), sortable: false, class: 'text-left', exportable: true },
                    {
                        key: 'amount',
                        label: this.$t('salesTransactions.amount'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.currency(value),
                    },
                    {
                        key: 'quantity',
                        label: this.$t('salesTransactions.quantity'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.number(value, 0),
                    },
                    { key: 'share', label: this.$t('salesTransactions.share'), sortable: false, class: 'text-right', exportable: false },
                ];
            },
            quantityShareLabels() {
                return [
                    `${this.$t('quantity2')}: {{value}} ${this.$t('salesSummaryQuantityItem')} ({{valuePercentFormatted}}) ${this.$t('ownProducts').toLowerCase()}`,
                    `${this.$t('quantity2')}: {{value}} ${this.$t('salesSummaryQuantityItem')} ({{valuePercentFormatted}}) ${this.$t('otherProducts').toLowerCase()}`,
                    `${this.$t('quantity2')}: {{value}} ${this.$t('salesSummaryQuantityItem')} ({{valuePercentFormatted}}) ${this.$t('unknownProducts').toLowerCase()}`,
                ];
            },
            items() {
                return this.rows.map(row => {
                    const totalQuantity = Number(row.quantity) || 0;
                    let ownQuantity = Number(row.ownQuantity);
                    let otherQuantity = Number(row.otherQuantity);
                    let unknownQuantity = Number(row.unknownQuantity);

                    if (!ownQuantity && !otherQuantity && !unknownQuantity && totalQuantity > 0) {
                        const sharePercent = Number(row.share) || 0;
                        const otherSharePercent = Number(row.otherShare) || 0;
                        ownQuantity = (sharePercent / 100) * totalQuantity;
                        otherQuantity = (otherSharePercent / 100) * totalQuantity;
                        unknownQuantity = Math.max(0, totalQuantity - ownQuantity - otherQuantity);
                    }

                    return {
                        ...row,
                        ownQuantity: ownQuantity || 0,
                        otherQuantity: otherQuantity || 0,
                        unknownQuantityComputed: unknownQuantity || 0,
                    };
                });
            },
        },
        watch: {
            spaceId() {
                this.groups = [];
                this.requestTransactions.resetAll?.();
                this.page = 1;
                this.fetchData();
            },
            apiParams: {
                deep: true,
                handler(newValue, oldValue) {
                    if (JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})) {
                        this.page = 1;
                        this.fetchData();
                    }
                },
            },
            page() {
                this.fetchData();
            },
            perPage() {
                this.page = 1;
                this.fetchData();
            },
            sortBy() {
                this.fetchData();
            },
            sortDirection() {
                this.fetchData();
            },
        },
        methods: {
            resetFilters() {
                this.conversion = 24;
                this.showProductGroupsResults = false;
                this.groups = [];
                this.page = 1;
                this.perPage = 10;
                this.sortBy = 'createdAt';
                this.sortDirection = 'DESC';
                this.selectedTransaction = null;
                this.showConversionPathModal = false;
                this.requestTransactions.resetAll?.();
                this.fetchData();
            },
            fetchGroups({ search }) {
                return fetchGroups({ requestGroups: this.requestGroups, spaceId: this.spaceId, search });
            },
            formatDuration(milliseconds) {
                return formatDuration(milliseconds);
            },
            async fetchData() {
                try {
                    if (!this.spaceId || !this.timeRange) return;
                    const { startDate, endDate } = this.timeRange;

                    const params = {
                        urlParams: { spaceId: this.spaceId },
                        requestParams: {
                            from: startDate,
                            to: endDate,
                            conversionTime: this.conversion === 'all' ? null : Number(this.conversion),
                            page: this.page,
                            perPage: this.perPage,
                            sort: this.sortBy === 'amount' ? 'transAmount' : this.sortBy,
                            order: this.sortDirection,
                        },
                    };
                    if (this.showProductGroupsResults && this.groups?.length) {
                        params.requestParams.groups = this.groups.join(',');
                    }
                    this.error = null;

                    const response = await this.requestTransactions.get(params);
                    this.rows = Array.isArray(response?.items) ? response.items : [];
                    this.total = Number(response?.max || 0);
                } catch (error) {
                    this.error = error;
                    this.rows = [];
                    this.total = 0;
                }
            },
            openConversionPath(transaction) {
                this.selectedTransaction = transaction;
                this.showConversionPathModal = true;
            },
        },
    };
</script>

<style scoped lang="scss">
    .nav-wrap {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }
</style>
